import React, { useMemo } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Download,
  History,
  Layers3,
  Loader2,
  RefreshCw,
  Upload,
} from 'lucide-react';

import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import type {
  GenerationImpactLayoutWorkspaceState,
  GenerationImpactScenarioSnapshot,
} from '../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../types/siteBoundary';
import {
  formatPublicationTime,
  readLayoutWorkspace,
  shortIntegrityHash,
  summarizeLayoutChanges,
} from './layoutPublishUi';

interface Props {
  workspace: GenerationImpactLayoutWorkspaceState | null;
  workspaceLoaded: boolean;
  scenario: GenerationImpactScenarioSnapshot | null;
  /** Current selected study parcels, matching the server publication boundary. */
  parcels?: readonly SiteBoundaryParcel[];
  publishBlockReason: string | null;
  remoteScenarioChanged: boolean;
  readOnly: boolean;
  reviewOpen: boolean;
  publishing: boolean;
  conflictMessage: string | null;
  publishError: string | null;
  onReviewOpenChange: (open: boolean) => void;
  onPublish: () => void;
  onExport: () => void;
  /** Optional Google Earth (.kml) export beside the GeoJSON export. */
  onExportKml?: () => void;
  onRefresh: () => void;
  /** Deep link to the Site Map tab where the published layers render. */
  onViewOnSiteMap?: () => void;
}

function ChangeGroup({
  tone,
  label,
  items,
}: {
  tone: 'add' | 'update' | 'remove';
  label: string;
  items: Array<{ key: string; label: string }>;
}): React.JSX.Element | null {
  if (items.length === 0) return null;
  const toneClass = tone === 'add'
    ? 'border-success/50 text-success'
    : tone === 'remove'
      ? 'border-warning/50 text-warning'
      : 'border-primary/50 text-primary';
  return (
    <div className="border border-border bg-background p-3 rounded-lg" data-testid={`layout-change-${tone}`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${toneClass} rounded-sm`}>{label}</span>
        <span className="text-[10px] text-text-muted">{items.length}</span>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-text-secondary">
        {items.map((item) => <li key={item.key}>— {item.label}</li>)}
      </ul>
    </div>
  );
}

export function GenerationScenarioStatusBadges({
  scenario,
  workspace,
  promotedScenarioId,
}: {
  scenario: GenerationImpactScenarioSnapshot;
  workspace: GenerationImpactLayoutWorkspaceState | null;
  promotedScenarioId: string | null;
}): React.JSX.Element | null {
  const active = readLayoutWorkspace(workspace).active;
  const isPublished = Boolean(
    active
      && active.scenarioId === scenario.id
      && active.inputHash === scenario.inputHash
      && active.resultHash === scenario.resultHash,
  );
  const isPromoted = scenario.id === promotedScenarioId;
  if (!isPublished && !isPromoted) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1" aria-label="Scenario status">
      {isPublished && (
        <span className="type-meta border border-success/40 bg-success/5 px-1.5 py-0.5 font-bold uppercase tracking-wider text-success rounded-md">
          Published v{active?.version}
        </span>
      )}
      {isPromoted && (
        <span className="type-meta border border-primary/40 bg-primary/5 px-1.5 py-0.5 font-bold uppercase tracking-wider text-primary rounded-md">
          Project Intent
        </span>
      )}
    </span>
  );
}

export function GenerationImpactLayoutPublishing({
  workspace,
  workspaceLoaded,
  scenario,
  parcels,
  publishBlockReason,
  remoteScenarioChanged,
  readOnly,
  reviewOpen,
  publishing,
  conflictMessage,
  publishError,
  onReviewOpenChange,
  onPublish,
  onExport,
  onExportKml,
  onRefresh,
  onViewOnSiteMap,
}: Props): React.JSX.Element {
  const view = useMemo(() => readLayoutWorkspace(workspace), [workspace]);
  const preview = useMemo(() => {
    try {
      return { summary: scenario ? summarizeLayoutChanges(scenario, workspace, parcels) : null, error: null };
    } catch (error) {
      return { summary: null, error: `Layout review unavailable: ${(error as Error).message}` };
    }
  }, [scenario, workspace, parcels]);
  const summary = preview.summary;
  const displayVersion = summary?.samePublishedScenario
    ? view.active?.version ?? 1
    : summary?.nextVersion ?? ((view.active?.version ?? 0) + 1);
  const reviewDisabled = Boolean(readOnly || !workspaceLoaded || publishBlockReason || preview.error || publishing);
  const recentVersions = useMemo(
    () => [...(workspace?.recent ?? [])].sort((left, right) => right.version - left.version).slice(0, 4),
    [workspace?.recent],
  );

  return (
    <>
      <section
        className="border border-border bg-card rounded-md"
        aria-labelledby="generation-layout-publish-heading"
        data-testid="generation-layout-publication-panel"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-muted">
              <Layers3 className="h-3.5 w-3.5 text-primary" aria-hidden /> Site Map layout
            </p>
            <h2 id="generation-layout-publish-heading" className="mt-1 text-sm font-bold text-text-primary">
              Draft → published version
            </h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={publishing}
            aria-label="Refresh layout publication status"
            data-testid="layout-publication-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <div className="grid items-stretch gap-0 md:grid-cols-[minmax(0,1fr)_44px_minmax(0,1fr)]">
          <div className="p-4" data-testid="layout-draft-state">
            <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Selected draft</span>
            {scenario ? (
              <>
                <strong className="mt-1 block truncate text-sm text-text-primary">{scenario.name}</strong>
                <span className="mt-1 block text-[10px] text-text-muted">
                  Saved {formatPublicationTime(scenario.updatedAt)} · result {shortIntegrityHash(scenario.resultHash)}
                </span>
              </>
            ) : (
              <p className="mt-1 text-xs text-text-muted">Save and select a scenario to prepare a map layout.</p>
            )}
          </div>

          <div className="hidden items-center justify-center border-x border-border bg-background text-text-muted md:flex" aria-hidden>
            <ArrowRight className="h-4 w-4" />
          </div>

          <div className="border-t border-border p-4 md:border-t-0" data-testid="layout-published-state">
            <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">Published to Site Map</span>
            {!workspaceLoaded ? (
              <p className="mt-1 flex items-center gap-2 text-xs text-text-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading publication state…</p>
            ) : view.active ? (
              <>
                <strong className="mt-1 flex items-center gap-2 text-sm text-text-primary">
                  <CheckCircle2 className="h-4 w-4 text-success" /> Layout v{view.active.version}
                </strong>
                <span className="mt-1 block text-[10px] text-text-muted">
                  {view.active.scenarioName ?? view.active.scenarioId} · {view.activeLayers.length} layer{view.activeLayers.length === 1 ? '' : 's'} · {formatPublicationTime(view.active.publishedAt)}
                </span>
                {onViewOnSiteMap && (
                  <button
                    type="button"
                    onClick={onViewOnSiteMap}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid="layout-view-on-site-map"
                  >
                    View on site map <ArrowRight className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </>
            ) : (
              <p className="mt-1 text-xs text-text-muted">No versioned layout is published.</p>
            )}
          </div>
        </div>

        {recentVersions.length > 0 && (
          <div className="border-t border-border px-4 py-3" data-testid="layout-version-ledger">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-text-muted">
                <History className="h-3 w-3" aria-hidden /> Recent immutable versions
              </span>
              <span className="text-[9px] text-text-muted">Latest {recentVersions.length}</span>
            </div>
            <ol className="grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4 rounded-md" aria-label="Recent layout versions">
              {recentVersions.map((publication) => {
                const active = publication.publicationId === view.active?.publicationId;
                return (
                  <li key={publication.publicationId} className="min-w-0 bg-background p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <b className="text-[10px] text-text-primary">v{publication.version}</b>
                      {active && <span className="type-meta border border-success/40 px-1 py-0.5 font-bold uppercase tracking-wider text-success rounded-md">Active</span>}
                    </div>
                    <span className="mt-1 block truncate text-[10px] text-text-secondary">{publication.scenarioName}</span>
                    <span className="mt-0.5 block truncate text-[9px] text-text-muted">{formatPublicationTime(publication.publishedAt)}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {(view.legacyUnversioned || conflictMessage || remoteScenarioChanged) && (
          <div className="space-y-2 border-t border-border px-4 py-3">
            {view.legacyUnversioned && (
              <p className="flex gap-2 border-l-2 border-warning bg-warning/5 px-3 py-2 text-xs text-text-secondary">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" /> Legacy unversioned generation layers will be retired by the first versioned publish.
              </p>
            )}
            {conflictMessage && (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-l-2 border-warning bg-warning/5 px-3 py-2 text-xs text-text-secondary" data-testid="layout-publication-conflict">
                <span><b className="text-text-primary">Review required.</b> {conflictMessage}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => onReviewOpenChange(true)} disabled={Boolean(publishBlockReason)}>
                  Review latest
                </Button>
              </div>
            )}
            {remoteScenarioChanged && (
              <p role="alert" className="border-l-2 border-warning bg-warning/5 px-3 py-2 text-xs text-text-secondary" data-testid="layout-scenario-conflict">
                This saved scenario changed elsewhere. Your working draft is preserved. Select the saved scenario again to load its latest revision, or clone this draft before saving.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
          <p className="max-w-2xl text-[10px] leading-relaxed text-text-muted" data-testid="layout-publish-block-reason">
            {publishBlockReason ?? preview.error ?? 'Review the managed layer replacement before publishing. CAD, KMZ, manual, and provider layers remain unchanged.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onExport} disabled={view.activeLayers.length === 0} data-testid="layout-export-published">
              <Download className="h-3.5 w-3.5" /> Export {view.active ? `v${view.active.version}` : 'published'} GeoJSON
            </Button>
            {onExportKml && (
              <Button type="button" variant="outline" size="sm" onClick={onExportKml} disabled={view.activeLayers.length === 0} data-testid="layout-export-published-kml">
                <Download className="h-3.5 w-3.5" /> Export {view.active ? `v${view.active.version}` : 'published'} KML
              </Button>
            )}
            <Button type="button" variant="mission-primary" size="sm" onClick={() => onReviewOpenChange(true)} disabled={reviewDisabled} data-testid="layout-review-publish">
              <Upload className="h-3.5 w-3.5" />
              {summary?.samePublishedScenario ? `Review published v${view.active?.version}` : `Review & publish v${displayVersion}`}
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={reviewOpen} onOpenChange={(open) => !publishing && onReviewOpenChange(open)}>
        <DialogContent size="lg" className="rounded-none border-border bg-background font-mono" data-testid="layout-publish-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-primary">
              <History className="h-3.5 w-3.5" /> Version-controlled Site Map change
            </div>
            <DialogTitle className="text-lg font-bold text-text-primary">
              {summary?.samePublishedScenario ? `Verify layout v${view.active?.version}` : `Publish layout v${displayVersion}`}
            </DialogTitle>
            <DialogDescription>
              Review the exact managed scope before replacing the active Generation &amp; Impact layout.
            </DialogDescription>
          </DialogHeader>

          {preview.error && <p role="alert" className="text-xs text-warning">{preview.error}</p>}

          {scenario && summary && (
            <div className="space-y-4">
              <div className="grid gap-px border border-border bg-border sm:grid-cols-2 rounded-md">
                <div className="bg-card p-3">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">From</span>
                  <strong className="mt-1 block text-xs text-text-primary">{view.active ? `Layout v${view.active.version}` : 'No published layout'}</strong>
                  <span className="mt-1 block text-[10px] text-text-muted">{view.active?.scenarioName ?? 'Empty Site Map slot'} · {summary.currentCount} managed layer{summary.currentCount === 1 ? '' : 's'}</span>
                </div>
                <div className="bg-card p-3">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted">To</span>
                  <strong className="mt-1 block text-xs text-text-primary">{scenario.name}</strong>
                  <span className="mt-1 block text-[10px] text-text-muted">Saved {formatPublicationTime(scenario.updatedAt)} · result {shortIntegrityHash(scenario.resultHash)} · {summary.proposedCount} managed layer{summary.proposedCount === 1 ? '' : 's'}</span>
                </div>
              </div>

              {summary.samePublishedScenario ? (
                <p className="flex gap-2 border-l-2 border-success bg-success/5 px-3 py-2 text-xs text-text-secondary" data-testid="layout-no-scenario-delta">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> This saved scenario and its integrity hashes already match the active publication. Retrying is idempotent and will not create a duplicate version.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-3" aria-label="Layout change summary">
                  <ChangeGroup tone="add" label="Add" items={summary.added} />
                  <ChangeGroup tone="update" label="Update" items={summary.updated} />
                  <ChangeGroup tone="remove" label="Retire" items={summary.removed} />
                </div>
              )}

              <div className="border border-border bg-card p-3 text-xs leading-relaxed text-text-secondary rounded-lg">
                <b className="text-text-primary">Managed replacement boundary:</b> only Generation &amp; Impact planning blocks, located receptors, and screening noise contours are replaced. CAD, KMZ, manual, document-derived, and provider layers stay unchanged.
              </div>
              <p className="text-[10px] leading-relaxed text-text-muted">
                This is planning geometry, not construction CAD. The server re-checks the scenario timestamp and input/result hashes, and rejects the publish if the active layout changed during review.
              </p>

              {publishError && (
                <p role="alert" className="border-l-2 border-danger bg-danger/5 px-3 py-2 text-xs text-danger" data-testid="layout-publish-error">
                  {publishError}
                </p>
              )}
            </div>
          )}

          <DialogFooter className="rounded-none">
            <Button type="button" variant="outline" size="sm" onClick={() => onReviewOpenChange(false)} disabled={publishing}>
              Cancel
            </Button>
            <Button type="button" variant="mission-primary" size="sm" onClick={onPublish} disabled={reviewDisabled || !scenario || !summary} data-testid="layout-publish-confirm">
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {publishing
                ? `Publishing v${displayVersion}…`
                : summary?.samePublishedScenario
                  ? `Verify v${view.active?.version}`
                  : `Publish v${displayVersion}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
